import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Sections|Import|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamSectionCatalog,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamSectionsImport';
export type SPCatalogsExamSectionsImportLocNames =
	| `${LocNameStart}Title`
	| `${LocNameStart}NotificationImportSuccess`
	| `${LocNameStart}ButtonHideInstructions`
	| `${LocNameStart}ButtonShowInstructions`
	| `${LocNameStart}ButtonDownloadTemplate`
	| `${LocNameStart}ButtonImport`
	| `${LocNameStart}ButtonUpsert`
	| `${LocNameStart}ContentImportIntroduction`
	| `${LocNameStart}ContentImportInstructions`
	| `${LocNameStart}NotificationInvalidHighMidLow`
	| `${LocNameStart}NotificationCutLessThanMax`
	| `${LocNameStart}NotificationMissingHighOrMax`
	| `${LocNameStart}NotificationCreateHasId`;

export const spCatalogsExamSectionImportLocs: LocDefs<SPCatalogsExamSectionsImportLocNames> = {
	SPCatalogsExamSectionsImportTitle: {
		defaultValue: 'Instructions',
		description: 'Import title',
		displayPath: `${pathStart}Title`,
		displayType: 'Title',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Title.Import',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportNotificationImportSuccess: {
		defaultValue: 'Successfully imported exam sections',
		description: 'Import success notification message',
		displayPath: `${pathStart}Import Success`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.NotificationMessage.ImportSuccess',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportButtonHideInstructions: {
		defaultValue: 'Hide Instructions',
		description: 'Hide Instructions button',
		displayPath: `${pathStart}Hide Instructions`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.HideInstructions',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportButtonShowInstructions: {
		defaultValue: 'Show Instructions',
		description: 'Show Instructions button',
		displayPath: `${pathStart}Show Instructions`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.ShowInstructions',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportButtonDownloadTemplate: {
		defaultValue: 'Download Import Template',
		description: 'Download Import Template button',
		displayPath: `${pathStart}Download Import Template`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.DownloadTemplate',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportButtonImport: {
		defaultValue: 'Import Sections',
		description: 'Import New Exam Sections from CSV button',
		displayPath: `${pathStart}Import`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.Import',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportButtonUpsert: {
		defaultValue: 'Update Sections',
		description: 'Update Exam Sections from CSV button',
		displayPath: `${pathStart}Update`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportNotificationCreateHasId: {
		defaultValue:
			'ID column found. Please remove the column to create a new section or use the update import.',
		description: 'Message shown when an item has an ID in the create import.',
		displayPath: `${pathStart}Create Has ID`,
		displayType: 'Notification',
		...sharedProperties,
	},
	SPCatalogsExamSectionsImportContentImportIntroduction: {
		defaultValue:
			'<p>To add new sections, use the Import Sections Button. A sample import file can be downloaded from the download import template button. File headers are listed in the import instructions below. Extra column headers and their corresponding information will be ignored.</p>  <p>New sections can be imported via CSV file or by using the "Add Exam Section" form. Exam sections can be modified using the Update Sections Button.</p>',
		description: 'Import introduction content',
		displayPath: `${pathStart}Import introduction`,
		displayType: 'Content',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Content.ImportIntroduction',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.ExamSectionCatalog,
		portal: 'SP',
	},
	SPCatalogsExamSectionsImportContentImportInstructions: {
		defaultValue:
			'<h3>Import Instructions</h3>  <p>The import system uses CSV files. The file must contain the following fields to successfully import:</p>  <ul>  <li>Form</li>  <li>Section name</li>  <li>Section label</li>  <li>High score</li>  <li>Mid score</li>  <li>Low score</li>  <li>Cut score</li>  <li>Max score</li>  </ul>  <p>The following fields are optional:</p>  <ul>  <li>ID</li><li>Coverage</li>  </ul>  <p><strong>Definitions:</strong></p>  <ul>  <li>ID: The ID of the exam section.</li><li>Form: The published exam form name.</li>  <li>Section name: The published exam section name.</li>  <li>Section label: The section name displayed on the score report.</li>  <li>Coverage: The percentage of the total exam this section covers. It can be a range (i.e. 24 - 32%) or an empty string, if applicable.</li>  <li>High score: The passing score for the first section on the score report. Any exam section equal to or greater than this value will be marked on the first section of the score report.</li>  <li>Mid score: The passing score for the second section on the score report. Any exam section equal to or greater than this value will be marked on the second section of the score report.</li>  <li>Low score: The passing score for the third section on the score report. Any exam section equal to or greater than this value will be marked on the third section of the score report.</li>  <li>Cut score: The passing raw score for this section of the exam.</li>  <li>Max score: The maximum raw score for this section of the exam.</li>  </ul> <p><strong>Update Instructions</strong></p> <p>The update button will add or insert new sections based on whether or not the ID column exists. If the ID column is set, that section will be updated. If the ID column does not exist, that section will be added to the exam. All other column rules for the import also are in effect when updating.</p>',
		description: 'Import instructions content',
		displayPath: `${pathStart}Import instructions`,
		displayType: 'Content',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Content.ImportInstructions',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.ExamSectionCatalog,
		portal: 'SP',
	},
	SPCatalogsExamSectionsImportNotificationInvalidHighMidLow: {
		defaultValue: 'High, Mid, and Low scores must be in the correct order.',
		description: 'Invalid high, mid, and low scores text.',
		displayPath: `${pathStart}Invalid High, Mid, and Low Scores`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ScoresNumeric',
		shared: false,
		feature: HubFeature.ExamSectionCatalog,
		portal: 'SP',
	},
	SPCatalogsExamSectionsImportNotificationCutLessThanMax: {
		defaultValue: 'Max score must be greater than or equal to cut score.',
		description: 'Invalid cut score text.',
		displayPath: `${pathStart}Invalid Cut Score`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.MaxGreaterThanCut',
		shared: false,
		feature: HubFeature.ExamSectionCatalog,
		portal: 'SP',
	},
	SPCatalogsExamSectionsImportNotificationMissingHighOrMax: {
		defaultValue: 'High or Max score is required.',
		description: 'Missing high or max score text.',
		displayPath: `${pathStart}Missing High or Max Score`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ScoresRequired',
		shared: false,
		feature: HubFeature.ExamSectionCatalog,
		portal: 'SP',
	},
};

export type SPCatalogsExamSectionsImportLocType = LocMap<SPCatalogsExamSectionsImportLocNames>;
